/* ============================================================================
 * api.js — the single fetch wrapper for the whole app. Direct port of the
 * original shell.js `Shell.api`, including its status handling:
 *   401  -> session expired: notify the auth layer to bounce to /login
 *   423  -> daily usage cap hit: notify the auth layer to show the lock screen
 *   !ok  -> decode FastAPI's structured `detail` dict into a friendly message
 *
 * Framework-agnostic: it throws ApiError and calls registered handlers that the
 * AuthContext wires up (so this file never imports React).
 * ========================================================================== */

export class ApiError extends Error {
  constructor(message, status, data) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.data = data;
  }
}

let onUnauthorized = null; // () => void   (set by AuthProvider)
let onLockout = null; // (resetEpoch) => void

export function setAuthHandlers({ unauthorized, lockout }) {
  onUnauthorized = unauthorized;
  onLockout = lockout;
}

export async function api(path, opts = {}) {
  const res = await fetch(path, {
    headers: { "Content-Type": "application/json" },
    ...opts,
    body:
      opts.body && typeof opts.body !== "string"
        ? JSON.stringify(opts.body)
        : opts.body,
  });

  if (res.status === 401) {
    if (onUnauthorized) onUnauthorized();
    throw new ApiError("unauthorized", 401, null);
  }

  if (res.status === 423) {
    // Daily usage exhausted — body carries reset_epoch for the countdown.
    let data = null;
    try {
      data = await res.json();
    } catch {
      /* no body */
    }
    if (onLockout) onLockout((data && data.reset_epoch) || null);
    throw new ApiError("daily_limit_reached", 423, data);
  }

  let data = null;
  try {
    data = await res.json();
  } catch {
    /* no body */
  }

  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    if (data) {
      if (typeof data.error === "string") msg = data.error;
      else if (typeof data.detail === "string") msg = data.detail;
      else if (data.detail && typeof data.detail === "object") {
        const d = data.detail;
        if (d.error === "quota_reached")
          msg = `Quota reached: ${d.resource} (${d.current}/${d.limit}). Ask an admin to raise the limit.`;
        else if (d.error === "flag_denied")
          msg = `Not allowed: ${d.flag}. Ask an admin.`;
        else if (d.error === "pair_not_allowed")
          msg = `Pair not allowed: ${d.exchange}:${d.symbol}. Ask an admin to add it to your allowlist.`;
        else msg = d.error || msg;
      }
    }
    throw new ApiError(msg, res.status, data);
  }
  return data;
}

/* Convenience verbs. */
export const apiGet = (path) => api(path);
export const apiPost = (path, body) => api(path, { method: "POST", body });
export const apiPut = (path, body) => api(path, { method: "PUT", body });
export const apiDelete = (path) => api(path, { method: "DELETE" });
